<template>
  <!-- Row: Chart -->
  <div
    :id="'div' + divName"
    class="row f-row"
    v-bind:class="[isLarge ? 'table_chart_height mt-1' : 'mt-1']"
    style="overflow-y: auto;"
  >
    <div class="row f-row" style="height:100%; width:100%;">
      <div class="col col-12">
        <div
          :style="{ 'padding-right': (isLarge ? 40 : 30) + 'px!important' }"
          style="padding-left: 1px"
        >
          <table
            class="table table-sm table-borderless curve_analysis_table"
            :class="isTotals && 'table__totals'"
          >
            <thead class="table-headers text-center align-center thead">
              <tr v-if="!isLarge">
                <th
                  :style="{
                    width:
                      Math.round(
                        isLarge ? divWidth / 12 : (4 * divWidth) / 12,
                      ) + 'px!important',
                  }"
                >
                  Date range
                </th>
                <td>
                  {{ dateValue }}
                </td>
              </tr>
              <tr
                v-for="(dat, idx) in tableFormat"
                :key="idx"
                :class="{
                  'div-prices': dat.divP,
                  'div-quantity': dat.divQ,
                }"
              >
                <template v-if="!dat.isDetails || showDetails">
                  <th
                    :class="{
                      ['text__prices' + divName]:
                        tableFormat.some((d) => d.divP) && idx === 0,
                      ['text__quantities' + divName]:
                        idx === tableFormat.length - 1,
                    }"
                    :style="{
                      width:
                        Math.round(
                          isLarge ? divWidth / 12 : (4 * divWidth) / 12,
                        ) + 'px!important',
                    }"
                  >
                    {{ dat.label }} ({{ dat.unit
                    }}<span v-if="dat.sUnit !== ''">/{{ dat.sUnit }}</span
                    >)
                  </th>
                  <td
                    v-for="(item, index) in zoomedData"
                    v-bind:key="index"
                    :class="{
                      tableHovered:
                        (formatVars['userHovered']['date'] === +item.date &&
                          formatVars['userHovered']['variable'] ===
                            dat['elementName'] &&
                          formatVars['userHovered']['hovered']) ||
                        (formatVars['userHovered']['fromChart'] &&
                          formatVars['userHovered']['date'] === +item.date &&
                          formatVars['userHovered']['variable'] ===
                            dat.gElementName),
                    }"
                    :style="{
                      'background-color': dat['back'],
                      'border-bottom': '1px solid ' + dat['bdr'],
                    }"
                    @mouseover="onTableHover(dat, item, true, index)"
                    @mouseleave="onTableHover(dat, item, false, index)"
                  >
                    <span>
                      {{
                        Number.isNaN(item[dat.field])
                          ? "-"
                          : item != null
                          ? dat.formatF(item[dat.field])
                          : ""
                      }}</span
                    >
                    <span v-if="dat.secondary && zoomedData.length < 16">
                      ({{ item != null ? item[dat.sField] : "" }})
                    </span>
                  </td>
                </template>
              </tr>
            </thead>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { D3Chart } from "@/_core/charts/D3Chart";
import { D3HoverPlanning } from "@/_core/charts/D3ChartAnalyticsHover";
import {
  colorExpo,
  colorExpoAlpha4,
  colorFwd,
  colorFwdAlpha2,
  colorHedge,
  colorHedgeAlpha,
  colorNet,
  colorNetAlpha2,
  currencyFormat,
  formatM,
  formatPercentSm,
  whiteGray,
  whiteGrayAlpha3,
} from "@/_core/_helpers/_parametric_tools";
import { format, select } from "d3";

export default {
  name: "AnalyticsTable",
  props: {
    colorTheme: { required: true, type: Boolean, default: true },
    divName: { required: false, default: "table" },
    fieldNames: {},
    isLarge: { required: false, type: Boolean, default: true },
    isTotals: { required: false, type: Boolean, default: false },
    loadingAll: { required: true, type: Boolean, default: false },
    noDataStr: { required: true, default: "Get Information" },
    renderChartSignal: { required: true, default: 0 },
    unit: { required: true, default: "n.a" },
    showDetails: { required: true, type: Boolean, default: false },
    sUnit: { required: true, default: "n.a" },
    zoomedData: { required: true, default: () => [] },
    formatVar: {
      default: {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
      },
    },
  },
  components: {},
  data() {
    return {
      currencyFormat: format(currencyFormat),
      D3Chart: null,
      D3HoverPlanning: null,
      dateFormat: "%b %y", // TODO: que cambie
      divWidth: 100,
      expandFRow: false, // TODO: que cambie
      formatVars: {
        budgetColor: colorExpo,
        budgetBColor: colorExpoAlpha4,
        fixColor: colorFwd,
        fixBColor: colorFwdAlpha2,
        mktColor: whiteGray,
        mktBColor: whiteGrayAlpha3,
        netColor: colorNet,
        netBColor: colorNetAlpha2,
        percent: formatPercentSm,
        quantity: formatM,
        rate: format(",.4r"),
        ratioColor: colorHedge,
        ratioBColor: colorHedgeAlpha,
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
      },
      minMaxYAxis: [-5, 0],
      svgNode: null,
    };
  },
  computed: {
    dateValue() {
      return this.zoomedData[0] != null
        ? Object.keys(this.zoomedData[0]).indexOf("date") !== -1
          ? this.zoomedData[0]["date"]
          : "date"
        : "date";
    },
    tableFormat() {
      return [
        {
          back: this.formatVars["budgetBColor"](this.colorTheme),
          bdr: this.formatVars["budgetColor"](this.colorTheme),
          formatF: this.formatVars["rate"],
          elementName: "RATEE",
          field: this.fieldNames["fldBudget"],
          isDetails: false,
          label: "Budget P",
          secondary: false,
          sField: "",
          unit: this.unit,
          sUnit: this.sUnit,
          gElementName: "RATES",
          divP: true,
        },
        {
          back: this.formatVars["mktBColor"](this.colorTheme),
          bdr: this.formatVars["mktColor"](this.colorTheme),
          formatF: this.formatVars["rate"],
          elementName: "RATEE45",
          field: this.fieldNames["fldMkt"],
          isDetails: false,
          label: "Mkt P",
          secondary: true,
          sField: this.fieldNames["fldMktPerc"],
          unit: this.unit,
          sUnit: this.sUnit,
          gElementName: "RATES",
          divP: true,
        },
        {
          back: this.formatVars["fixBColor"](this.colorTheme),
          bdr: this.formatVars["fixColor"],
          formatF: this.formatVars["rate"],
          elementName: "RATEH",
          field: this.fieldNames["fldFixed"],
          isDetails: false,
          label: "Fixed P",
          secondary: true,
          sField: this.fieldNames["fldFixedPerc"],
          unit: this.unit,
          sUnit: this.sUnit,
          gElementName: "RATES",
          divP: true,
        },
        {
          back: this.formatVars["netBColor"](this.colorTheme),
          bdr: this.formatVars["netColor"](this.colorTheme),
          formatF: this.formatVars["rate"],
          elementName: "RATEN",
          field: this.fieldNames["fldNet"],
          isDetails: false,
          label: "Net P",
          secondary: true,
          sField: this.fieldNames["fldNetPerc"],
          unit: this.unit,
          sUnit: this.sUnit,
          gElementName: "RATES",
          divP: true,
        },
        {
          back: this.formatVars["budgetBColor"](this.colorTheme),
          bdr: this.formatVars["budgetColor"](this.colorTheme),
          formatF: this.formatVars["quantity"],
          elementName: "PLANB2",
          field: "budgetQ",
          isDetails: true,
          label: "Budget Q",
          secondary: true,
          sField: "budgetQPerc",
          unit: this.sUnit,
          sUnit: "",
          gElementName: "PLAN",
          divQ: true,
        },
        {
          back: this.formatVars["budgetBColor"](this.colorTheme),
          bdr: this.formatVars["budgetColor"](this.colorTheme),
          formatF: this.formatVars["quantity"],
          elementName: "PLANB",
          field: "forecastQ",
          isDetails: false,
          label: "Forecast Q",
          secondary: false,
          sField: "",
          unit: this.sUnit,
          sUnit: "",
          gElementName: "PLAN",
          divQ: true,
        },
        {
          back: this.formatVars["ratioBColor"](this.colorTheme),
          bdr: this.formatVars["ratioColor"](this.colorTheme),
          formatF: this.formatVars["quantity"],
          elementName: "PLANP",
          field: "ratioQ",
          isDetails: false,
          label: "Ratio Q",
          secondary: true,
          sField: "ratioQPerc",
          unit: this.sUnit,
          sUnit: "",
          gElementName: "PLAN",
          divQ: true,
        },
        {
          back: this.formatVars["fixBColor"](this.colorTheme),
          bdr: this.formatVars["fixColor"],
          formatF: this.formatVars["quantity"],
          elementName: "stackedBar1PLANH",
          field: "fixedQ",
          isDetails: false,
          label: "Fixed Q",
          secondary: true,
          sField: "fixedQPerc",
          unit: this.sUnit,
          sUnit: "",
          gElementName: "PLAN",
          divQ: true,
        },
      ];
    },
  },
  created() {
    this.D3Chart = new D3Chart("an_table-div", "svg-an_table");
    this.D3HoverPlanning = new D3HoverPlanning();
  },
  mounted() {
    this.calculateDivWidth();
  },
  watch: {
    renderChartSignal() {
      this.calculateDivWidth();
    },
    formatVar: {
      handler(newVal) {
        this.getChangeTableHover(newVal);
      },
      deep: true,
    },
  },
  methods: {
    calculateDivWidth() {
      let element = document.getElementById("div" + this.divName);
      if (element != null) {
        Object.assign(this, {
          divWidth: element.clientWidth,
        });
        const { textP, textQ, topP, topQ, leftP, leftQ } = this.getTextValues(
          this.tableFormat.length,
        );
        this.generateTextStyle(
          textP,
          `text__prices${this.divName}`,
          topP,
          leftP,
        );
        this.generateTextStyle(
          textQ,
          `text__quantities${this.divName}`,
          topQ,
          leftQ,
        );
      }
    },
    getTextValues(tableFormatLength) {
      const textInfo = {
        short: {
          textP: "Px",
          textQ: "Qty",
          topP: 0,
          topQ: 25,
          leftP: 0,
          leftQ: 25,
        },
        long: {
          textP: "PRICES",
          textQ: "QUANTITIES",
          topP: 50,
          topQ: -70,
          leftP: 15,
          leftQ: 30,
        },
      };

      return tableFormatLength < 3 ? textInfo.short : textInfo.long;
    },
    onTableHover(dat, item, isHovered) {
      select(".bar" + dat["elementName"] + "" + +item.date).classed(
        "hoved",
        isHovered,
      );
      select(`.${dat["elementName"]}${+item.date}`).classed("hoved", isHovered);

      Object.assign(this.formatVars, {
        userHovered: {
          date: +item.date,
          variable: dat["elementName"],
          hovered: isHovered,
          fromChart: false,
        },
      });
    },
    getChangeTableHover(val) {
      Object.assign(this.formatVars, {
        userHovered: {
          date: val.date,
          variable: val.variable,
          hovered: false,
          fromChart: val.fromChart,
        },
      });
    },
    deleteChart() {
      select("#an_table-div")
        .selectAll(".tooltip")
        .remove();
      select("#an_table-div")
        .select("svg > *")
        .remove();
    },
    generateTextStyle(content, selector, top, left) {
      const styleCSS = document.createElement("style");
      const divContainerTable = select(`.curve_analysis_table`).node()
        .clientWidth;
      const divContainerTableTotals = this.isTotals
        ? select(`.curve_analysis_table.table__totals`).node().clientWidth
        : "";

      styleCSS.innerHTML = `
        .${selector} {
          position: relative;
        }
        th.${selector}::after {
          content: "${content}";
          position: absolute;
          transform: rotate(-90deg);
          top: ${top}%;
          transform-origin: center;
          white-space: nowrap;
          font-size: 12px;
          color: var(--white-gray);
          z-index: 1;
          text-shadow: 1px 1px 1px rgb(180 180 180 / 20%);
        }
        th#date-range::after {
          content: "${content}";
          position: absolute;
          transform: rotate(-90deg);
          top: ${top}%;
          left: ${this.divWidth / 12}px;
          transform-origin: center;
          white-space: nowrap;
          font-size: 12px;
          color: var(--white-gray);
          z-index: 1;
          text-shadow: 1px 1px 1px rgb(180 180 180 / 20%);
        }
        .curve_analysis_table thead tr th.${selector}::after {
          left: calc(${divContainerTable}px - ${left}px) !important;
        }
        .curve_analysis_table.table__totals thead tr th.${selector}::after {
          left: calc(${divContainerTableTotals}px - ${left}px) !important;
        }
    `;

      document.head.appendChild(styleCSS);
    },
  },
};
</script>

<style>
table tr.div-prices th {
  background: var(--lightbackground-alpha);
}
@media (max-width: 1280px) {
  table tr th.text__quantities::after {
    top: 0%;
  }
}
</style>
